import { useState, useCallback, useEffect } from 'react';
import ToolLayout from '../../components/layout/ToolLayout';
import { Button, Card, CardContent, CopyButton, Select } from '@devkit/ui';
import { Clock, Calendar } from 'lucide-react';
import { trackToolExecute, trackCopy } from '@devkit/analytics';

const timezoneOptions = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
];

const unitOptions = [
  { value: 'seconds', label: 'Seconds' },
  { value: 'milliseconds', label: 'Milliseconds' },
];

export default function TimestampConverterPage() {
  const [timestamp, setTimestamp] = useState('1704067200');
  const [dateString, setDateString] = useState('2024-01-01T00:00:00Z');
  const [timezone, setTimezone] = useState('UTC');
  const [unit, setUnit] = useState('seconds');
  const [result, setResult] = useState<{ date: Date; formatted: string; relative: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Get current timestamp
  const getCurrentTimestamp = useCallback(() => {
    const now = Date.now();
    setTimestamp(unit === 'seconds' ? Math.floor(now / 1000).toString() : now.toString());
    trackToolExecute('timestamp-converter', 'now');
    convertTimestamp();
  }, [unit]);

  // Convert timestamp to date
  const convertTimestamp = useCallback(() => {
    setError(null);

    const ts = parseInt(timestamp);
    if (isNaN(ts)) {
      setError('Invalid timestamp');
      setResult(null);
      return;
    }

    const ms = unit === 'seconds' ? ts * 1000 : ts;
    const date = new Date(ms);

    if (isNaN(date.getTime())) {
      setError('Invalid date from timestamp');
      setResult(null);
      return;
    }

    trackToolExecute('timestamp-converter', 'timestamp-to-date');

    setResult({
      date,
      formatted: date.toLocaleString('en-US', { timeZone: timezone }),
      relative: getRelativeTime(date),
    });
  }, [timestamp, unit, timezone]);

  // Convert date string to timestamp
  const convertDate = useCallback(() => {
    setError(null);
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      setError('Invalid date format');
      return;
    }

    const ts = unit === 'seconds' ? Math.floor(date.getTime() / 1000) : date.getTime();
    setTimestamp(ts.toString());
    trackToolExecute('timestamp-converter', 'date-to-timestamp');
    convertTimestamp();
  }, [dateString, unit, convertTimestamp]);

  // Get relative time string
  const getRelativeTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const absDiff = Math.abs(diff);
    const isPast = diff > 0;

    const units = [
      { name: 'year', ms: 365 * 24 * 60 * 60 * 1000 },
      { name: 'month', ms: 30 * 24 * 60 * 60 * 1000 },
      { name: 'day', ms: 24 * 60 * 60 * 1000 },
      { name: 'hour', ms: 60 * 60 * 1000 },
      { name: 'minute', ms: 60 * 1000 },
      { name: 'second', ms: 1000 },
    ];

    for (const u of units) {
      const value = Math.floor(absDiff / u.ms);
      if (value >= 1) {
        const suffix = isPast ? 'ago' : 'from now';
        return `${value} ${u.name}${value > 1 ? 's' : ''} ${suffix}`;
      }
    }

    return 'just now';
  };

  useEffect(() => {
    convertTimestamp();
  }, [timezone, unit]);

  return (
    <ToolLayout slug="timestamp-converter">
      <Card>
        <CardContent className="p-6">
          {/* Options */}
          <div className="flex flex-wrap gap-4 mb-6 pb-6 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-400">Unit:</label>
              <Select
                options={unitOptions}
                value={unit}
                onChange={setUnit}
                className="w-40"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-400">Timezone:</label>
              <Select
                options={timezoneOptions}
                value={timezone}
                onChange={setTimezone}
                className="w-48"
              />
            </div>
          </div>

          {/* Timestamp Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Unix Timestamp ({unit})
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={timestamp}
                onChange={(e) => setTimestamp(e.target.value)}
                placeholder="Enter timestamp..."
                className="flex-1 px-4 py-2.5 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary"
              />
              <Button onClick={getCurrentTimestamp} variant="outline" className="gap-2">
                <Clock className="w-4 h-4" />
                Now
              </Button>
              <Button onClick={convertTimestamp} className="gap-2">
                Convert
              </Button>
            </div>
          </div>

          {/* Date Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Date & Time (ISO 8601)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={dateString}
                onChange={(e) => setDateString(e.target.value)}
                placeholder="2024-01-01T00:00:00Z"
                className="flex-1 px-4 py-2.5 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary"
              />
              <Button onClick={convertDate} variant="secondary" className="gap-2">
                <Calendar className="w-4 h-4" />
                To Timestamp
              </Button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="space-y-4">
              <div className="p-4 bg-surface rounded-lg border border-slate-700">
                <h3 className="text-sm font-medium text-slate-300 mb-3">Converted Date</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Formatted:</span>
                    <div className="flex items-center gap-2">
                      <code className="text-sm text-white">{result.formatted}</code>
                      <CopyButton text={result.formatted} onCopy={() => trackCopy('timestamp-converter')} className="text-xs" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">ISO 8601:</span>
                    <code className="text-sm text-primary">{result.date.toISOString()}</code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Unix:</span>
                    <code className="text-sm text-secondary">
                      {Math.floor(result.date.getTime() / 1000)} (seconds)
                    </code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Milliseconds:</span>
                    <code className="text-sm text-accent">{result.date.getTime()} (ms)</code>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-primary/10 rounded-lg border border-primary/30">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-primary">Relative Time</span>
                </div>
                <p className="text-lg text-white">{result.relative}</p>
              </div>

              {/* Calendar Output */}
              <div className="p-4 bg-surface rounded-lg border border-slate-700">
                <h3 className="text-sm font-medium text-slate-300 mb-3">Calendar View</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-400">Year:</span>
                    <span className="ml-2 text-white">{result.date.getFullYear()}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Month:</span>
                    <span className="ml-2 text-white">{result.date.getMonth() + 1}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Day:</span>
                    <span className="ml-2 text-white">{result.date.getDate()}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Hour:</span>
                    <span className="ml-2 text-white">{result.date.getHours()}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Minute:</span>
                    <span className="ml-2 text-white">{result.date.getMinutes()}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Second:</span>
                    <span className="ml-2 text-white">{result.date.getSeconds()}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </ToolLayout>
  );
}
